<template>
  <div class="offer-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('offers.title') }}</h2>
      <b-button variant="primary" @click="createOffer">
        <i class="fas fa-plus mr-2"></i>{{ $t('offers.create_button') }}
      </b-button>
    </div>

    <CrudTable :columns="fields" :data-sources="offers" :loader="isLoading" list-url="/offers" ref="action-table">
      <template #cell(name)="data">
        <div class="d-flex align-items-center">
          <div class="offer-info">
            <div class="font-weight-bold">{{ data.item.name || 'N/A' }}</div>
          </div>
        </div>
      </template>

      <template #cell(type)="data">
        <div>
          <span class="badge" :class="getTypeBadgeClass(data.item.type)">
            {{ getTypeDisplayName(data.item.type) }}
          </span>
        </div>
      </template>

      <template #cell(value)="data">
        <div>{{ formatValue(data.item) }}</div>
      </template>

      <template #cell(hotel_name)="data">
        <div>{{ data.item.hotel && data.item.hotel.name ? data.item.hotel.name : $t('offers.all_hotels') }}</div>
      </template>

      <template #cell(is_active)="data">
        <div>
          <span class="badge" :class="data.item.is_active ? 'bg-success' : 'bg-secondary'">
            {{ data.item.is_active ? $t('offers.active') : $t('offers.inactive') }}
          </span>
        </div>
      </template>

      <template #cell(validity)="data">
        <div class="small">
          <div v-if="data.item.starts_at">
            From: {{ formatDate(data.item.starts_at) }}
          </div>
          <div v-if="data.item.expires_at">
            To: {{ formatDate(data.item.expires_at) }}
          </div>
          <div v-if="!data.item.starts_at && !data.item.expires_at">
            {{ $t('offers.always_valid') }}
          </div>
        </div>
      </template>

      <template #cell(usage)="data">
        <div class="small">
          <div v-if="data.item.usage_limit">
            {{ $t('offers.used') }}: {{ data.item.usages_count || 0 }} {{ $t('offers.of') }} {{ data.item.usage_limit }}
          </div>
          <div v-else>
            {{ $t('offers.unlimited') }}
          </div>
        </div>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex gap-1">
          <b-button size="sm" variant="info" @click="viewOffer(data.item)" :title="$t('offers.view_details')">
            <i class="fas fa-eye"></i>
          </b-button>
          <b-button size="sm" variant="primary" @click="editOffer(data.item)" :title="$t('offers.edit_offer')">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button size="sm" variant="danger" @click="confirmDelete(data.item)" :title="$t('offers.delete_offer')">
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>

    <ConfirmDeleteModal v-if="selectedItem" :show.sync="showDeleteModal" :item-id="selectedItem.id" delete-url="/offers"
      :message="$t('offers.confirm_delete_message', { name: selectedItem.name })"
      success-message="$t('offers.deleted_successfully')" error-message="$t('common.something_went_wrong')"
      @deleted="handleItemDeleted" />
  </div>
</template>

<script>
import { request } from '@/Util/Request';
import CrudTable from '@/components/CrudTable.vue';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal.vue';

export default {
  name: 'OfferList',
  components: {
    CrudTable,
    ConfirmDeleteModal,
  },
  data() {
    return {
      offers: [],
      isLoading: false,
      currentPage: 1,
      perPage: 10,
      sortBy: 'id',
      sortDesc: true,
      filters: {},
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0
      },
      showDeleteModal: false,
      selectedItem: null,
      fields: [
        { key: 'name', label: this.$t('offers.name'), sortable: true },
        { key: 'type', label: this.$t('offers.type'), sortable: true },
        { key: 'value', label: this.$t('offers.value'), sortable: true },
        { key: 'hotel_name', label: this.$t('offers.hotel_id'), sortable: true },
        { key: 'is_active', label: this.$t('offers.status'), sortable: true },
        { key: 'validity', label: this.$t('offers.validity_period'), sortable: false },
        { key: 'usage', label: this.$t('offers.usage_limit'), sortable: false },
        { key: 'actions', label: this.$t('offers.actions'), sortable: false }
      ]
    };
  },
  methods: {
    async fetchOffers() {
      try {
        this.isLoading = true;
        const response = await request({
          method: 'get',
          url: '/offers',
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            ...this.filters
          }
        });

        if (response.data && response.data.data) {
          this.offers = response.data.data;
          if (response.data.meta) {
            this.paginationInfo = {
              from: response.data.meta.from || 0,
              to: response.data.meta.to || 0,
              total: response.data.meta.total || 0
            };
          }
        } else {
          this.offers = [];
          this.paginationInfo = {
            from: 0,
            to: 0,
            total: 0
          };
        }
      } catch (error) {
        console.error('Error fetching offers:', error);
        this.offers = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0
        };
      } finally {
        this.isLoading = false;
      }
    },

    handleSearch(searchTerm) {
      this.filters.search = searchTerm;
      this.currentPage = 1;
      this.fetchOffers();
    },

    handleFilter(filters) {
      this.filters = { ...this.filters, ...filters };
      this.currentPage = 1;
      this.fetchOffers();
    },

    createOffer() {
      this.$router.push('/admin/offers/create');
    },

    viewOffer(offer) {
      this.$router.push(`/admin/offers/${offer.id}`);
    },

    editOffer(offer) {
      this.$router.push(`/admin/offers/${offer.id}/edit`);
    },

    confirmDelete(item) {
      this.selectedItem = item;
      this.showDeleteModal = true;
    },

    handleItemDeleted() {
      this.$refs['action-table'].refreshTableData();
      this.$bvToast.toast(this.$t('offers.deleted_successfully'), {
        title: this.$t('common.success'),
        variant: 'success',
        solid: true
      });
    },

    getTypeBadgeClass(type) {
      const classes = {
        'percentage': 'bg-info',
        'fixed': 'bg-primary',
        'free_night': 'bg-success',
        'upgrade': 'bg-warning',
        'package': 'bg-secondary'
      };
      return classes[type] || 'bg-secondary';
    },

    getTypeDisplayName(type) {
      const names = {
        'percentage': 'Percentage',
        'fixed': 'Fixed Amount',
        'free_night': 'Free Night',
        'upgrade': 'Room Upgrade',
        'package': 'Package Deal'
      };
      return names[type] || type;
    },

    formatValue(offer) {
      switch (offer.type) {
        case 'percentage':
          return `${offer.value}%`;
        case 'fixed':
          return `$${offer.value}`;
        case 'free_night':
          return '1 Free Night';
        case 'upgrade':
          return 'Room Upgrade';
        case 'package':
          return `$${offer.value} Package`;
        default:
          return offer.value;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    }
  },

  created() {
    this.fetchOffers();
  }
};
</script>

<style scoped>
.offer-list {
  padding: 20px;
}

.offer-info {
  flex: 1;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}
</style>